/**
 * Conversão do Markdown retornado pelo assistente (endpoint questionarTutorial)
 * em HTML, usando a lib "marked".
 *
 * Ajustes aplicados sobre o padrão da lib:
 * - links relativos apontam para a documentação;
 * - URLs com /spaces/ ficam só na base, sem o caminho interno do GitBook;
 * - links/imagens abrem em nova aba e têm o protocolo validado;
 * - HTML cru vindo na resposta é escapado, evitando injeção de script.
 */

import { marked } from "marked";
import DOMPurify from "dompurify";

const DOCS_BASE_URL = "https://meajuda.gitbook.io";

const escaparHtml = (texto) =>
  String(texto)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * URLs com "/spaces/" apontam para IDs internos do GitBook, que não abrem para
 * quem lê o painel. Nesses casos fica só a base (esquema + domínio), sem
 * caminho, query ou âncora.
 */
const removerCaminhoDeSpaces = (url) => {
  if (!/^https?:/i.test(url) || !/\/spaces\//i.test(url)) {
    return url;
  }

  try {
    return new URL(url).origin;
  } catch (e) {
    return url;
  }
};

const tratarUrl = (url) => {
  const limpa = String(url || "").trim();

  // Links relativos vindos da documentação (ex.: /spaces/.../pages/...)
  if (/^\//.test(limpa)) {
    return removerCaminhoDeSpaces(`${DOCS_BASE_URL}${limpa}`);
  }

  if (/^(https?:|mailto:|tel:|#)/i.test(limpa)) {
    return removerCaminhoDeSpaces(limpa);
  }

  // Qualquer outro protocolo (javascript:, data:, etc.) é descartado.
  return "";
};

const renderer = new marked.Renderer();

// "title" e "text" já chegam escapados da marked; só a URL precisa de escape.
renderer.link = (href, title, text) => {
  const url = tratarUrl(href);

  if (!url) {
    return text;
  }

  const titulo = title ? ` title="${title}"` : "";

  return `<a href="${escaparHtml(url)}"${titulo} target="_blank" rel="noopener noreferrer">${text}</a>`;
};

renderer.image = (href, title, text) => {
  const url = tratarUrl(href);

  if (!url) {
    return text || "";
  }

  const titulo = title ? ` title="${title}"` : "";

  return `<img src="${escaparHtml(url)}" alt="${text || ""}"${titulo}>`;
};

// HTML cru na resposta é exibido como texto, nunca interpretado.
renderer.html = (html) => escaparHtml(html);

marked.setOptions({
  renderer: renderer,
  gfm: true,
  breaks: true,
  headerIds: false,
  mangle: false,
});

export const markdownParaHtml = (markdown) => {
  if (!markdown) {
    return "";
  }

  const texto = String(markdown)
    .replace(/\r\n?/g, "\n")
    // A resposta usa "\" no fim da linha para forçar a quebra. Como a opção
    // "breaks" já quebra toda linha, a barra é removida: mantida, ela atrapalha
    // a leitura do negrito em linhas terminadas em ")**\".
    .replace(/\\\n/g, "\n");

  // O renderer acima já escapa HTML cru e valida as URLs; o DOMPurify é uma
  // segunda barreira, para o caso de algum trecho escapar dessas regras.
  return DOMPurify.sanitize(marked.parse(texto), {
    ADD_ATTR: ["target"],
  });
};

export default markdownParaHtml;
